// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../store/AppContext.js';
import { getAccountTransactions, formatAmount } from '../../lib/eventLog.js';
import {
  calculateCostBasis,
  calculateRealizedGainFromProceeds,
  getInvestmentAction,
  formatShares,
  SHARE,
  INVESTMENT_ACCOUNT_TYPES,
} from '../../lib/investment.js';

type CostBasisMethod = 'fifo' | 'average';

interface RealizedItem {
  security: string;
  acquiredDate: string;
  soldDate: string;
  shares: number;        // hundred-thousandths
  proceeds: number;      // cents
  basis: number;         // cents
  gainLoss: number;      // cents
  term: 'short' | 'long';
}

export function CapitalGainsReport() {
  const { t } = useTranslation(['investment', 'common', 'errors']);
  const { state } = useApp();
  const { ledger } = state;

  const currentYear = new Date().getFullYear();
  const [taxYear, setTaxYear] = useState(currentYear);
  const [method, setMethod] = useState<CostBasisMethod>('fifo');
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());

  const investmentAccounts = useMemo(
    () => [...ledger.accounts.values()].filter((a) => INVESTMENT_ACCOUNT_TYPES.has(a.type) && !a.placeholder),
    [ledger.accounts],
  );

  // Default: all investment accounts selected
  const effectiveAccounts = selectedAccounts.size === 0 ? investmentAccounts : investmentAccounts.filter((a) => selectedAccounts.has(a.id));

  const items: RealizedItem[] = useMemo(() => {
    const result: RealizedItem[] = [];

    for (const account of effectiveAccounts) {
      const allTxs = getAccountTransactions(ledger, account.id);
      const ticker = account.commodity?.ticker ?? account.name;

      // Find all sell transactions in the selected tax year
      const sellTxs = allTxs.filter((tx) => {
        const split = tx.splits.find((s) => s.accountId === account.id);
        if (!split) return false;
        const action = getInvestmentAction(tx, account.id);
        if (action !== 'sell') return false;
        return tx.date.startsWith(String(taxYear));
      });

      for (const sellTx of sellTxs) {
        const split = sellTx.splits.find((s) => s.accountId === account.id);
        if (!split || split.quantity >= 0) continue;

        const sharesToSell = -split.quantity;

        // Find lots BEFORE this sale to get correct FIFO basis
        const txsBeforeSale = allTxs.filter((t) => t.date < sellTx.date ||
          (t.date === sellTx.date && t.dateEntered < sellTx.dateEntered));
        const lotsBeforeSale = calculateCostBasis(account.id, txsBeforeSale);

        // Gross proceeds = opposite of split.amount (the cash received from the sale)
        // We look for the cash split to find actual proceeds
        const cashSplit = sellTx.splits.find((s) => s.accountId !== account.id && s.amount > 0 && s.quantity === s.amount);
        const grossProceeds = cashSplit ? cashSplit.amount : Math.abs(split.amount);

        if (method === 'fifo') {
          const gainResult = calculateRealizedGainFromProceeds(lotsBeforeSale, sharesToSell, grossProceeds, sellTx.date);

          for (const lotItem of gainResult.lotsConsumed) {
            const lotBasis = Math.round((lotItem.sharesConsumed * lotItem.lot.costPerShare) / SHARE);
            const lotProceeds = lotItem.gainLoss + lotBasis;
            result.push({
              security: ticker,
              acquiredDate: lotItem.lot.purchaseDate,
              soldDate: sellTx.date,
              shares: lotItem.sharesConsumed,
              proceeds: lotProceeds,
              basis: lotBasis,
              gainLoss: lotItem.gainLoss,
              term: lotItem.term,
            });
          }
        } else {
          // Average cost — treat the whole sale as one entry
          const avgLots = calculateCostBasis(account.id, txsBeforeSale); // same as lotsBeforeSale here, but with avg cost logic
          let totalRemaining = avgLots.reduce((s, l) => s + l.remainingShares, 0);
          let totalBasisRemaining = avgLots.reduce((s, l) => s + Math.round((l.remainingShares * l.costPerShare) / SHARE), 0);
          const avgCostPerShare = totalRemaining > 0 ? Math.round((totalBasisRemaining * SHARE) / totalRemaining) : 0;
          const saleBassis = Math.round((sharesToSell * avgCostPerShare) / SHARE);
          const gainLoss = grossProceeds - saleBassis;

          // For avg cost, classify by whether lots span >1 year (simplified: use oldest lot date)
          const oldestLot = lotsBeforeSale.find((l) => l.remainingShares > 0);
          const term = oldestLot
            ? (() => {
                const purchase = new Date(oldestLot.purchaseDate + 'T00:00:00');
                const sale = new Date(sellTx.date + 'T00:00:00');
                const oneYearAfter = new Date(purchase);
                oneYearAfter.setFullYear(oneYearAfter.getFullYear() + 1);
                return sale >= oneYearAfter ? 'long' : 'short';
              })()
            : 'short';

          result.push({
            security: ticker,
            acquiredDate: oldestLot?.purchaseDate ?? '—',
            soldDate: sellTx.date,
            shares: sharesToSell,
            proceeds: grossProceeds,
            basis: saleBassis,
            gainLoss,
            term: term as 'short' | 'long',
          });
        }
      }
    }

    return result.sort((a, b) => a.soldDate.localeCompare(b.soldDate));
  }, [effectiveAccounts, ledger, taxYear, method]);

  const shortTermItems = items.filter((i) => i.term === 'short');
  const longTermItems = items.filter((i) => i.term === 'long');

  const shortTermProceeds = shortTermItems.reduce((s, i) => s + i.proceeds, 0);
  const shortTermBasis = shortTermItems.reduce((s, i) => s + i.basis, 0);
  const shortTermGain = shortTermItems.reduce((s, i) => s + i.gainLoss, 0);

  const longTermProceeds = longTermItems.reduce((s, i) => s + i.proceeds, 0);
  const longTermBasis = longTermItems.reduce((s, i) => s + i.basis, 0);
  const longTermGain = longTermItems.reduce((s, i) => s + i.gainLoss, 0);

  const totalGain = shortTermGain + longTermGain;

  function exportCsv() {
    const rows = [
      ['Security', 'Acquired', 'Sold', 'Shares', 'Proceeds', 'Basis', 'Gain/Loss', 'Term'],
      ...items.map((i) => [
        i.security,
        i.acquiredDate,
        i.soldDate,
        (i.shares / SHARE).toFixed(3),
        (i.proceeds / 100).toFixed(2),
        (i.basis / 100).toFixed(2),
        (i.gainLoss / 100).toFixed(2),
        i.term,
      ]),
    ];
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `capital-gains-${taxYear}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function renderTable(tableItems: RealizedItem[]) {
    if (tableItems.length === 0) {
      return <p className="text-sm text-text-muted py-4 text-center">{t('capitalGains.noItems')}</p>;
    }
    return (
      <table className="w-full text-xs border-collapse mb-2">
        <thead>
          <tr className="border-b border-border text-text-subtle">
            <th className="text-left py-1.5 px-2 font-medium">{t('capitalGains.csvHeaders.security')}</th>
            <th className="text-left py-1.5 px-2 font-medium">{t('capitalGains.csvHeaders.acquired')}</th>
            <th className="text-left py-1.5 px-2 font-medium">{t('capitalGains.csvHeaders.sold')}</th>
            <th className="text-right py-1.5 px-2 font-medium">{t('capitalGains.csvHeaders.shares')}</th>
            <th className="text-right py-1.5 px-2 font-medium">{t('capitalGains.csvHeaders.proceeds')}</th>
            <th className="text-right py-1.5 px-2 font-medium">{t('capitalGains.csvHeaders.basis')}</th>
            <th className="text-right py-1.5 px-2 font-medium">{t('capitalGains.csvHeaders.gainLoss')}</th>
          </tr>
        </thead>
        <tbody>
          {tableItems.map((item, idx) => (
            <tr key={idx} className={`border-b border-border ${idx % 2 === 0 ? 'bg-bg' : 'bg-bg-surface'}`}>
              <td className="py-1.5 px-2 font-mono font-medium text-text">{item.security}</td>
              <td className="py-1.5 px-2 font-mono text-text-muted">{item.acquiredDate}</td>
              <td className="py-1.5 px-2 font-mono text-text-muted">{item.soldDate}</td>
              <td className="py-1.5 px-2 text-right font-mono text-text">{formatShares(item.shares)}</td>
              <td className="py-1.5 px-2 text-right font-mono text-text">{formatAmount(item.proceeds, 'USD')}</td>
              <td className="py-1.5 px-2 text-right font-mono text-text-muted">{formatAmount(item.basis, 'USD')}</td>
              <td className={`py-1.5 px-2 text-right font-mono font-medium ${item.gainLoss >= 0 ? 'text-positive' : 'text-negative'}`}>
                {item.gainLoss >= 0 ? '+' : ''}{formatAmount(item.gainLoss, 'USD')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-md font-semibold text-text">{t('capitalGains.title')}</h2>
        <button
          onClick={exportCsv}
          className="px-3 py-1 border border-border text-sm text-text-muted hover:bg-bg-elevated hover:text-text transition-colors"
        >
          {t('capitalGains.exportCsv')}
        </button>
      </div>

      {/* Options */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs text-text-muted">{t('capitalGains.taxYear')}</label>
          <select
            value={taxYear}
            onChange={(e) => setTaxYear(Number(e.target.value))}
            className="bg-bg border border-border text-text text-sm px-2 py-1 focus:border-accent focus:outline-none"
          >
            {Array.from({ length: 6 }, (_, i) => currentYear - i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs text-text-muted">{t('capitalGains.method')}</label>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as CostBasisMethod)}
            className="bg-bg border border-border text-text text-sm px-2 py-1 focus:border-accent focus:outline-none"
          >
            <option value="fifo">{t('capitalGains.fifo')}</option>
            <option value="average">{t('capitalGains.averageCost')}</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs text-text-muted">{t('capitalGains.accounts')}</label>
          <select
            value=""
            onChange={(e) => {
              const val = e.target.value;
              if (val === 'all') {
                setSelectedAccounts(new Set());
              } else if (val) {
                setSelectedAccounts((prev) => {
                  const next = new Set(prev);
                  if (next.has(val)) next.delete(val);
                  else next.add(val);
                  return next;
                });
              }
            }}
            className="bg-bg border border-border text-text text-sm px-2 py-1 focus:border-accent focus:outline-none"
          >
            <option value="all">{selectedAccounts.size === 0 ? t('capitalGains.allAccounts') : t('capitalGains.accountsSelected', { count: selectedAccounts.size })}</option>
            {investmentAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {selectedAccounts.has(a.id) ? '✓ ' : '  '}{a.commodity?.ticker ?? a.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="flex items-center justify-center h-32 text-text-muted">
          <p className="text-sm">{t('capitalGains.noGains', { taxYear })}</p>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <div className="px-2 py-1 text-xs font-medium text-text-muted uppercase tracking-wide bg-bg-surface border border-border-strong mb-0">
              {t('capitalGains.shortTerm')}
            </div>
            <div className="border border-border border-t-0">
              {renderTable(shortTermItems)}
              <div className="px-2 py-2 border-t border-border bg-bg-surface flex gap-6 text-xs font-medium">
                <span className="text-text-muted">{t('capitalGains.shortTermTotal')}</span>
                <span className="font-mono">{formatAmount(shortTermProceeds, 'USD')} {t('capitalGains.proceedsSuffix')}</span>
                <span className="font-mono text-text-muted">{formatAmount(shortTermBasis, 'USD')} {t('capitalGains.basisSuffix')}</span>
                <span className={`font-mono font-semibold ${shortTermGain >= 0 ? 'text-positive' : 'text-negative'}`}>
                  {shortTermGain >= 0 ? '+' : ''}{formatAmount(shortTermGain, 'USD')}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <div className="px-2 py-1 text-xs font-medium text-text-muted uppercase tracking-wide bg-bg-surface border border-border-strong mb-0">
              {t('capitalGains.longTerm')}
            </div>
            <div className="border border-border border-t-0">
              {renderTable(longTermItems)}
              <div className="px-2 py-2 border-t border-border bg-bg-surface flex gap-6 text-xs font-medium">
                <span className="text-text-muted">{t('capitalGains.longTermTotal')}</span>
                <span className="font-mono">{formatAmount(longTermProceeds, 'USD')} {t('capitalGains.proceedsSuffix')}</span>
                <span className="font-mono text-text-muted">{formatAmount(longTermBasis, 'USD')} {t('capitalGains.basisSuffix')}</span>
                <span className={`font-mono font-semibold ${longTermGain >= 0 ? 'text-positive' : 'text-negative'}`}>
                  {longTermGain >= 0 ? '+' : ''}{formatAmount(longTermGain, 'USD')}
                </span>
              </div>
            </div>
          </div>

          <div className="border border-border bg-bg-surface p-4 mb-4">
            <h3 className="text-xs font-medium text-text-muted uppercase tracking-wide mb-3">{t('capitalGains.summary', { taxYear })}</h3>
            <div className="space-y-1 text-sm font-mono">
              <div className="flex justify-between">
                <span className="text-text-muted">{t('capitalGains.shortTermGains')}</span>
                <span className={shortTermGain >= 0 ? 'text-positive' : 'text-negative'}>
                  {shortTermGain >= 0 ? '+' : ''}{formatAmount(shortTermGain, 'USD')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">{t('capitalGains.longTermGains')}</span>
                <span className={longTermGain >= 0 ? 'text-positive' : 'text-negative'}>
                  {longTermGain >= 0 ? '+' : ''}{formatAmount(longTermGain, 'USD')}
                </span>
              </div>
              <div className="flex justify-between border-t border-border pt-1 mt-1 font-semibold">
                <span className="text-text">{t('capitalGains.totalNetGain')}</span>
                <span className={totalGain >= 0 ? 'text-positive' : 'text-negative'}>
                  {totalGain >= 0 ? '+' : ''}{formatAmount(totalGain, 'USD')}
                </span>
              </div>
            </div>
          </div>

          <p className="text-xs text-text-subtle text-center">
            {t('capitalGains.disclaimer')}
          </p>
        </>
      )}
    </div>
  );
}
